import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createContext,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";

import type { ColorScheme, ColorTheme } from "@/app/theme";
import { parseColorScheme, parseColorTheme } from "@/app/theme";
import { useAuth } from "@/features/auth/lib/use-auth";
import {
	type ContentSettings,
	contentSettingsKey,
	contentSettingsPatch,
	getContentSettings,
	updateContentSettings,
} from "@/features/content/api";
import { getMutationErrorMessage } from "@/shared/api/errors";
import type { Season } from "@/shared/api/types";

export type SeasonContextValue = {
	season: Season;
	seasonSwitch: boolean;
	isPending: boolean;
	applySeason: (next: Season) => void;
	applySeasonSwitch: (next: boolean) => void;
};

export type TillAppearanceContextValue = {
	scheme: ColorScheme;
	theme: ColorTheme;
	isPending: boolean;
	applyScheme: (next: ColorScheme) => void;
	applyTheme: (next: ColorTheme) => void;
};

export const SeasonContext = createContext<SeasonContextValue | null>(null);
export const TillAppearanceContext =
	createContext<TillAppearanceContextValue | null>(null);

function parseSettings(settings: ContentSettings): {
	season: Season;
	cardColorScheme: ColorScheme;
	cardColorTheme: ColorTheme;
	seasonSwitch: boolean;
} {
	return {
		season: settings.activeSeason,
		cardColorScheme: parseColorScheme(settings.cardColorScheme),
		cardColorTheme: parseColorTheme(settings.cardColorTheme),
		seasonSwitch: settings.seasonSwitch,
	};
}

export function ContentSettingsProvider({ children }: { children: ReactNode }) {
	const { t } = useTranslation();
	const { isPortalAdmin, isAuthenticated } = useAuth();
	const queryClient = useQueryClient();
	const [season, setSeason] = useState<Season>("winter");
	const [cardColorScheme, setCardColorScheme] = useState<ColorScheme>("light");
	const [cardColorTheme, setCardColorTheme] = useState<ColorTheme>("brand");
	const [seasonSwitch, setSeasonSwitch] = useState(true);

	const query = useQuery({
		queryKey: contentSettingsKey,
		queryFn: getContentSettings,
		enabled: isAuthenticated && isPortalAdmin,
		retry: false,
	});

	useEffect(() => {
		if (!query.data) {
			return;
		}
		const parsed = parseSettings(query.data);
		setSeason(parsed.season);
		setCardColorScheme(parsed.cardColorScheme);
		setCardColorTheme(parsed.cardColorTheme);
		setSeasonSwitch(parsed.seasonSwitch);
	}, [query.data]);

	useEffect(() => {
		document.documentElement.dataset.season = season;
	}, [season]);

	const mutation = useMutation({
		mutationFn: updateContentSettings,
		onSuccess: (settings) => {
			queryClient.setQueryData(contentSettingsKey, settings);
			const parsed = parseSettings(settings);
			setSeason(parsed.season);
			setCardColorScheme(parsed.cardColorScheme);
			setCardColorTheme(parsed.cardColorTheme);
			setSeasonSwitch(parsed.seasonSwitch);
		},
		onError: async (error) => {
			notifications.show({
				color: "red",
				title: t("app.settingsFailed"),
				message: await getMutationErrorMessage(error),
			});
		},
	});

	const persist = useCallback(
		(patch: Partial<ContentSettings>) => {
			if (!isPortalAdmin) {
				return;
			}
			mutation.mutate(
				contentSettingsPatch(
					{
						activeSeason: season,
						cardColorScheme,
						cardColorTheme,
						seasonSwitch,
					},
					patch,
				),
			);
		},
		[
			cardColorScheme,
			cardColorTheme,
			isPortalAdmin,
			mutation,
			season,
			seasonSwitch,
		],
	);

	const isPending = query.isLoading || mutation.isPending;

	const seasonValue = useMemo<SeasonContextValue>(
		() => ({
			season,
			seasonSwitch,
			isPending,
			applySeason: (next: Season) => {
				if (next === season) {
					return;
				}
				setSeason(next);
				persist({ activeSeason: next });
			},
			applySeasonSwitch: (next: boolean) => {
				if (next === seasonSwitch) {
					return;
				}
				setSeasonSwitch(next);
				persist({ seasonSwitch: next });
			},
		}),
		[isPending, persist, season, seasonSwitch],
	);

	const tillValue = useMemo<TillAppearanceContextValue>(
		() => ({
			scheme: cardColorScheme,
			theme: cardColorTheme,
			isPending,
			applyScheme: (next: ColorScheme) => {
				const parsed = parseColorScheme(next);
				if (parsed === cardColorScheme) {
					return;
				}
				setCardColorScheme(parsed);
				persist({ cardColorScheme: parsed });
			},
			applyTheme: (next: ColorTheme) => {
				const parsed = parseColorTheme(next);
				if (parsed === cardColorTheme) {
					return;
				}
				setCardColorTheme(parsed);
				persist({ cardColorTheme: parsed });
			},
		}),
		[cardColorScheme, cardColorTheme, isPending, persist],
	);

	return (
		<SeasonContext.Provider value={seasonValue}>
			<TillAppearanceContext.Provider value={tillValue}>
				{children}
			</TillAppearanceContext.Provider>
		</SeasonContext.Provider>
	);
}
